import { css } from 'aphrodite';
import * as React from 'react';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { AIMarkdownResponse } from '../../../../web/components-v2/ai/AiMarkdownResponse';
import { Button } from '../../../../web/components/Button';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { SparklesIcon } from '../../../../web/components/svgs/icons/SparklesIcon';
import { XIcon } from '../../../../web/components/svgs/icons/XIcon';
import { brandPrimary, charmGray } from '../../../../web/styles/colors';
import { useTelephony } from '../../../contexts/telephony';
import { useQueue } from '../../../hooks/queue';
import { useAISummary } from '../../../hooks/useAISummary';
import { AidaDraggable } from '../../AidaDraggable';
import { DraggableIcon } from '../../svgs/icons/audio/DraggableIcon';
import { useTwilio } from '../context';
import { styleSheet } from './styles';

export const SalesAi = () => {
	return <SalesAiContent />;
};

const SalesAiContent = () => {
	const { phoneCall, lastPhoneCall } = useTelephony();
	const { startNewRealtimeTranscriptionUtterance, realtimeTranscriptionUtterances } = useTwilio();
	const queue = useQueue();

	// AI Summary functionality (HubSpot data is prefetched by Queue and cached by useLeadAIContext)
	const {
		aiResponse,
		isLoading,
		isError,
		hasFetchedFeedback,
		resetPostCallFeedback,
		sendFollowUpQuestion,
		conversationId,
	} = useAISummary(realtimeTranscriptionUtterances, {
		phoneCallId: phoneCall?.id || lastPhoneCall?.id,
		companyId: queue?.companyId,
		leadServedId: queue?.leadServedId,
		source: queue?.leadServedSource,
	});
	const [showAIModal, setShowAIModal] = React.useState(false);
	const [followUpQuestion, setFollowUpQuestion] = React.useState('');
	const [conversationMessages, setConversationMessages] = React.useState<
		{ role: 'user' | 'assistant'; content: string }[]
	>([]);

	const [aiModalDragging, setAiModalDragging] = React.useState<{
		active?: boolean;
		x?: number;
		y?: number;
	}>({ active: false, x: 0, y: 0 });

	// Load AI modal position from localStorage on component mount
	React.useEffect(() => {
		try {
			const savedPosition = localStorage.getItem('aiModalPosition');
			if (savedPosition) {
				const { x, y } = JSON.parse(savedPosition);
				setAiModalDragging(prev => ({ ...prev, x, y }));
			}
		} catch (error) {
			console.error('Error loading AI modal position from localStorage:', error);
		}
	}, []);

	const onAiModalDrag = (event: DraggableEvent, { x, y }: DraggableData) => {
		event.stopPropagation();
		setAiModalDragging({ active: true, x, y });
	};

	const onAiModalDragStop = (_: DraggableEvent, { x, y }: DraggableData) => {
		const { active } = aiModalDragging;
		if (active) {
			setAiModalDragging({ active: false, x, y });

			// Save position to localStorage when drag stops
			try {
				localStorage.setItem('aiModalPosition', JSON.stringify({ x, y }));
			} catch (error) {
				console.error('Error saving AI modal position to localStorage:', error);
			}
		}
	};

	React.useEffect(() => {
		if (phoneCall?.id && !lastPhoneCall?.id) {
			startNewRealtimeTranscriptionUtterance(phoneCall);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneCall, lastPhoneCall]);

	// Reset AI feedback when call is no longer complete
	React.useEffect(() => {
		if (!lastPhoneCall) {
			resetPostCallFeedback();
			setShowAIModal(false);
		}
	}, [lastPhoneCall, resetPostCallFeedback]);

	// Track when AI response is complete to add to conversation history
	const [lastAiResponseCompleted, setLastAiResponseCompleted] = React.useState(false);
	React.useEffect(() => {
		if (!isLoading && aiResponse && hasFetchedFeedback && !lastAiResponseCompleted) {
			setConversationMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
			setLastAiResponseCompleted(true);
		}
	}, [isLoading, aiResponse, hasFetchedFeedback, lastAiResponseCompleted]);

	// Reset tracking when starting a new AI session
	React.useEffect(() => {
		if (!hasFetchedFeedback) {
			setLastAiResponseCompleted(false);
			setConversationMessages([]);
		}
	}, [hasFetchedFeedback]);

	const handleSendFollowUp = React.useCallback(() => {
		if (!followUpQuestion.trim() || !conversationId) return;

		// Add user message to conversation
		setConversationMessages(prev => [...prev, { role: 'user', content: followUpQuestion }]);

		// Send the question
		sendFollowUpQuestion(followUpQuestion);

		// Clear the input
		setFollowUpQuestion('');

		// Reset completion tracking for the new response
		setLastAiResponseCompleted(false);
	}, [followUpQuestion, conversationId, sendFollowUpQuestion]);

	if (!phoneCall && !lastPhoneCall) return null;

	return (
		<>
			{/* AI Summary Draggable Panel */}
			{showAIModal && (
				<AidaDraggable
					onDrag={onAiModalDrag}
					onStop={onAiModalDragStop}
					defaultPosition={{ x: aiModalDragging.x, y: aiModalDragging.y }}
					position={{ x: aiModalDragging.x, y: aiModalDragging.y }}
					handle='.ai-feedback-drag-handle'
					isDraggable={true}
				>
					<div className={css(styleSheet.aiModalContainer)}>
						<div className={css(styleSheet.aiModalHeader)}>
							<div className={css(styleSheet.aiModalHeaderContent)}>
								<SparklesIcon className={css(styleSheet.modalIcon)} />
								<h3 className={css(styleSheet.modalTitle)}>Post-Call AI Feedback</h3>
							</div>
							<div className={css(styleSheet.aiModalHeaderActions)}>
								<div className='ai-feedback-drag-handle'>
									<DraggableIcon fill={brandPrimary} />
								</div>
								<button onClick={() => setShowAIModal(false)} className={css(styleSheet.iconButton)}>
									<XIcon color={charmGray} height={12} width={12} />
								</button>
							</div>
						</div>
						<div className={css(styleSheet.aiModalContent)}>
							{!conversationId ? (
								<div className={css(styleSheet.loadingContainerWithColor)}>
									<LoadingSpinner type='small' />
									<div className={css(styleSheet.loadingText)}>Creating AI Conversation...</div>
								</div>
							) : (
								<>
									<div className={css(styleSheet.conversationContainer)}>
										{conversationMessages.map((message, index) => (
											<div
												key={index}
												className={css(
													styleSheet.messageContainer,
													message.role === 'user' ? styleSheet.userMessage : styleSheet.assistantMessage
												)}
											>
												<div className={css(styleSheet.messageRole)}>
													{message.role === 'user' ? 'You' : 'AI Assistant'}
												</div>
												<div className={css(styleSheet.messageContent)}>
													<AIMarkdownResponse>{message.content}</AIMarkdownResponse>
												</div>
											</div>
										))}

										{/* Show streaming AI response */}
										{aiResponse && (!lastAiResponseCompleted || conversationMessages.length === 0) && (
											<div className={css(styleSheet.messageContainer, styleSheet.assistantMessage)}>
												<div className={css(styleSheet.messageRole)}>
													AI Assistant
													{isLoading && (
														<span className={css(styleSheet.streamingIndicator)}>
															<LoadingSpinner type='small' />
															Streaming...
														</span>
													)}
												</div>
												<div className={css(styleSheet.messageContent)}>
													<AIMarkdownResponse>{aiResponse}</AIMarkdownResponse>
												</div>
											</div>
										)}

										{/* Show loading when waiting for response */}
										{isLoading && !aiResponse && (
											<div className={css(styleSheet.messageContainer, styleSheet.assistantMessage)}>
												<div className={css(styleSheet.messageRole)}>AI Assistant</div>
												<div className={css(styleSheet.loadingContainer)}>
													<LoadingSpinner type='small' />
													<div className={css(styleSheet.loadingText)}>
														{conversationMessages.length === 0 ? 'Analyzing your call...' : 'Thinking...'}
													</div>
												</div>
											</div>
										)}

										{/* Show error or empty state */}
										{!isLoading && !aiResponse && conversationMessages.length === 0 && (
											<div className={css(styleSheet.messageContainer, styleSheet.assistantMessage)}>
												<div className={css(styleSheet.messageRole)}>AI Assistant</div>
												<div className={css(styleSheet.messageContent)}>
													{isError
														? 'Sorry, there was an error generating feedback. Please try again.'
														: 'Waiting for AI response...'}
												</div>
											</div>
										)}
									</div>

									{/* Follow-up question input - only show when not loading */}
									{!isLoading && (
										<div className={css(styleSheet.inputContainer)}>
											<textarea
												className={css(styleSheet.textarea)}
												value={followUpQuestion}
												onChange={e => setFollowUpQuestion(e.target.value)}
												placeholder='Ask a follow-up question about the call...'
												disabled={isLoading}
											/>
											<div className={css(styleSheet.buttonContainer)}>
												<Button
													kind='secondary'
													size='small'
													onClick={handleSendFollowUp}
													disabled={isLoading || !followUpQuestion.trim()}
												>
													Send Question
												</Button>
											</div>
										</div>
									)}
								</>
							)}
						</div>
					</div>
				</AidaDraggable>
			)}
		</>
	);
};
